import React from 'react';
import { StageSection, Prose, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Transactions & Instructions',
    tagline: 'Declaring every account upfront is what makes parallelism possible.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - why declaring accounts upfront enables parallel execution
//   Q1 - atomicity of multiple instructions bundled in one transaction
//   Q2 - what makes an account a required signer
export const blocks: StageBlock[] = [
    content('anatomy', () => (
        <StageSection gradientBorder eyebrow="Stage 4 of 8" title="An instruction names a program and every account it touches">
            <Prose>
                A Solana transaction bundles one or more instructions. Each instruction specifies the program to
                invoke, a complete list of the accounts it will read or write (each flagged read-only or writable,
                signer or not), and instruction data: the arguments for that call.
            </Prose>
            <FactGrid items={[
                ['Program ID', 'Which program this instruction invokes.'],
                ['Account list', 'Every account the instruction touches, declared upfront with read/write and signer flags.'],
                ['Instruction data', 'Arbitrary bytes: the arguments the program reads to decide what to do.'],
            ]} />
        </StageSection>
    )),

    content('why-parallel', () => (
        <StageSection title="This is what makes parallel execution possible">
            <Prose>
                Because every account an instruction will touch is declared before execution even begins, the
                runtime can look at a batch of pending transactions and see exactly which ones share an account and
                which ones don't. Transactions that touch completely disjoint sets of accounts can safely run at the
                same time; only transactions that actually conflict over the same account need to be serialized.
                This is the direct answer to Stage 1's question of how Solana gets its throughput.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('atomicity', () => (
        <StageSection title="Multiple instructions, one atomic outcome">
            <Prose>
                A single transaction can bundle several instructions (even ones calling different programs) and
                they succeed or fail together. If any instruction in the transaction fails, every state change made
                by every instruction in that transaction is rolled back; there's no partial application.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('signers', () => (
        <StageSection title="Signers authorize, per instruction">
            <Prose>
                An account is only treated as a required signer if at least one instruction in the transaction lists
                it that way, and the transaction must include a valid signature from that account's key. A program
                checks these signer flags itself to decide whether a given account is actually authorized to take
                the action being requested. This check, done wrong, is exactly what the account-authority security
                track digs into.
            </Prose>
        </StageSection>
    )),

    question(2),
];
